import { type TracksSortState } from '@/entities/tracks'
import { type TrackModel } from '@/shared/api'
import { STRINGS } from '@/shared/config'
import { EmptyContent, HidingSpin, TableWrapper } from '@/shared/ui'
import { PrimaryButton } from '@/shared/ui'
import { SearchOutlined } from '@ant-design/icons'
import { Flex, Input, Modal, Table } from 'antd'
import { type TableProps } from 'antd/lib'
import { useUnit } from 'effector-react'

import {
  $isJingleSelectionModalOpen,
  $jinglesSelectionTablePage,
  $jinglesSelectionTablePageSize,
  $jinglesSelectionTableSearchInputValue,
  $jinglesSelectionTableSort,
  $selectedJingleIds,
  JinglesSelectionTableGate,
  jingleIdSelected,
  jingleSelectionModalClosed,
  jingleSelectionReset,
  jinglesSelectionTablePageChanged,
  jinglesSelectionTablePageReset,
  jinglesSelectionTableQuery,
  jinglesSelectionTableSearchChanged,
  jinglesSelectionTableSortChanged,
  jinglesSelectionTableSortReset,
} from '../model/jingle-selection-modal.model'

interface JingleSelectionModalProps {
  onApply: (jingleIds: string[]) => void
}
export function JingleSelectionModal({ onApply }: JingleSelectionModalProps) {
  const isOpen = useUnit($isJingleSelectionModalOpen)
  const close = useUnit(jingleSelectionModalClosed)

  const { pending, data, error } = useUnit(jinglesSelectionTableQuery)
  const reset = useUnit(jinglesSelectionTableQuery.reset)

  const sort = useUnit($jinglesSelectionTableSort)
  const changeSort = useUnit(jinglesSelectionTableSortChanged)
  const resetSort = useUnit(jinglesSelectionTableSortReset)

  const page = useUnit($jinglesSelectionTablePage)
  const changePage = useUnit(jinglesSelectionTablePageChanged)
  const resetPage = useUnit(jinglesSelectionTablePageReset)
  const pageSize = useUnit($jinglesSelectionTablePageSize)

  const search = useUnit($jinglesSelectionTableSearchInputValue)
  const changeSearch = useUnit(jinglesSelectionTableSearchChanged)

  const selectedIds = useUnit($selectedJingleIds)
  const resetSelection = useUnit(jingleSelectionReset)

  const selectRow = useUnit(jingleIdSelected)

  const columns: TableProps<TrackModel>['columns'] = [
    {
      title: 'Наименование',
      width: '100%',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder:
        sort.field === 'name'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (name, _) => name,
    },
  ]

  const onTableChange: TableProps<TrackModel>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    // Sort
    if (!Array.isArray(sorter)) {
      const field = sorter.field as string | undefined
      const order = sorter.order as 'ascend' | 'descend' | undefined

      if (field && order) {
        const sortOrder = order === 'ascend' ? 'asc' : 'desc'

        if (sort.field !== field || sort.order !== sortOrder) {
          changeSort({
            field: field as TracksSortState['field'],
            order: sortOrder,
          })
          resetPage()
        }
      } else if (sort.field !== null || sort.order !== null) {
        resetSort()
        resetPage()
      }
    }
    // Pagination
    if (pagination.current && pagination.pageSize) {
      if (pagination.current !== page) {
        changePage(pagination.current)
      }
    }
  }

  return (
    <Modal
      centered
      destroyOnHidden
      title="Выбор существующего джингла"
      open={isOpen}
      style={{ minWidth: '50%' }}
      onCancel={() => {
        close()
      }}
      footer={
        <PrimaryButton
          disabled={!selectedIds}
          onClick={() => {
            if (selectedIds && selectedIds.length) onApply(selectedIds)
            reset()
            resetSelection()
            close()
          }}
        >
          Добавить
        </PrimaryButton>
      }
    >
      <JinglesSelectionTableGate />
      <Flex vertical gap={10}>
        <Flex justify="end">
          <Input
            placeholder={STRINGS.placeholder_search_by_name_input}
            allowClear
            addonAfter={<SearchOutlined />}
            value={search || ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const searchString = event.target.value
              changeSearch(searchString)
            }}
            style={{
              width: 300,
            }}
          />
        </Flex>
        <HidingSpin spinning={pending || (!data?.items && !error)}>
          {data?.items && data.items.length > 0 ? (
            <TableWrapper>
              <Table
                sticky
                showSorterTooltip={false}
                rowKey={(record) => record.id}
                bordered
                columns={columns}
                dataSource={data.items}
                pagination={{
                  current: page,
                  pageSize: pageSize,
                  total: data.totalItems,
                  locale: { items_per_page: 'Джинглов на странице' },
                }}
                onChange={onTableChange}
                onRow={(record) => {
                  const isSelected = selectedIds.includes(record.id)
                  return {
                    onClick: () => {
                      selectRow(record.id)
                    },
                    style: isSelected
                      ? {
                          backgroundColor: '#F9F5FF',
                          color: '#7F45E5',
                          cursor: 'pointer',
                        }
                      : {
                          cursor: 'pointer',
                        },
                  }
                }}
              />
            </TableWrapper>
          ) : search?.length && search?.length > 0 ? (
            <EmptyContent description={STRINGS.nothing_was_found} />
          ) : (
            <EmptyContent description="Нет не выбранных сезонов" />
          )}
        </HidingSpin>
      </Flex>
    </Modal>
  )
}
